import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Smartphone, MapPin, CalendarClock } from "lucide-react";
import { useI18n } from "../i18n";
import { LOGO, GOOGLE_DIRECTIONS } from "../lib/site";
import AutoVideo from "./AutoVideo";

export default function HelmHero() {
  const { t } = useI18n();
  const heroRef = useRef(null);
  const wheelRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const hero = heroRef.current;
    const wheel = wheelRef.current;
    if (!hero || !wheel) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    let current = 0, target = 0, ticking = false;
    const step = () => {
      current += (target - current) * 0.08;
      wheel.style.setProperty("--helm-rotate", current.toFixed(2) + "deg");
      if (Math.abs(target - current) > 0.01) requestAnimationFrame(step);
      else ticking = false;
    };
    const read = () => {
      const rect = hero.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      const progress = total > 0 ? Math.min(Math.max(-rect.top / total, 0), 1) : 0;
      target = progress * 270; // half turn across the hero scroll
      if (!ticking) { ticking = true; requestAnimationFrame(step); }
    };
    window.addEventListener("scroll", read, { passive: true });
    window.addEventListener("resize", read);
    read();
    return () => { window.removeEventListener("scroll", read); window.removeEventListener("resize", read); };
  }, []);

  return (
    <section className="helm-hero" ref={heroRef} id="helm-hero" data-testid="helm-hero">
      <div className="helm-stage">
        <AutoVideo src={`${process.env.PUBLIC_URL || ""}/since1979.mp4`} poster="/hero/1.jpg" className="hero-bg-video" />
        <div className="hero-overlay" />
        <div className="hero-glow" />
        <div className="hero-fade-bottom" />
        <p className="font-sub text-amber-400 text-xs sm:text-sm tracking-[0.25em] uppercase mb-6 z-10 px-4 hero-anim hero-anim-1">{t.hero.eyebrow}</p>
        <div className="helm-float z-10">
          <div className="helm-wheel" ref={wheelRef} aria-hidden="true" style={{ backgroundImage: `url(${process.env.PUBLIC_URL || ""}/wheel.png)` }} />
          <div className="helm-logo">
            <img src={LOGO} alt="Logo Restaurante El Timón San Juan del Sur" fetchPriority="high" />
          </div>
        </div>
        <div className="z-10 mt-8 px-4">
          <div className="hero-anim hero-anim-2">
            <img src={`${process.env.PUBLIC_URL || ""}/wordmark.png`} alt="El Timón" fetchPriority="high" className="mx-auto w-[280px] sm:w-[360px] lg:w-[440px] max-w-[85vw] drop-shadow-[0_6px_18px_rgba(0,0,0,0.5)]" />
            <span className="block font-script text-2xl sm:text-3xl lg:text-4xl text-amber-400 leading-[1.05] mt-2">San Juan del Sur</span>
            <h1 className="sr-only">El Timón San Juan del Sur</h1>
          </div>
          <p className="font-sub text-slate-300 mt-3 text-base sm:text-lg hero-anim hero-anim-3">{t.hero.subtitle} · {t.common.since}</p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-7 hero-anim hero-anim-4">
            <button onClick={() => navigate("/carta")} data-testid="hero-view-menu-btn"
              className="cta-glow bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-7 py-3 rounded-full transition-all hover:scale-[1.03]">
              {t.common.viewMenu}
            </button>
            <button onClick={() => navigate("/menu")} data-testid="hero-order-now-btn"
              className="border border-amber-400/40 text-amber-300 hover:bg-amber-500/10 px-7 py-3 rounded-full transition-all inline-flex items-center gap-2">
              <Smartphone className="h-4 w-4" /> {t.common.orderNow}
            </button>
            <a href={GOOGLE_DIRECTIONS} target="_blank" rel="noreferrer" data-testid="hero-directions-btn"
              className="border border-teal-400/40 text-teal-300 hover:bg-teal-500/10 px-7 py-3 rounded-full transition-all inline-flex items-center gap-2">
              <MapPin className="h-4 w-4" /> {t.nav.directions}
            </a>
            <button onClick={() => navigate("/contact#reservar")} data-testid="hero-reserve-btn"
              className="border border-amber-400/40 text-amber-300 hover:bg-amber-500/10 px-7 py-3 rounded-full transition-all inline-flex items-center gap-2">
              <CalendarClock className="h-4 w-4" /> {t.common.reserve}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
